import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import DataCard from "../components/DataCard";
import DataTable from "../components/DataTable";
import Tabs from "../components/Tabs";
import CustomModal from "../components/CustomModal";

import {
    deleteLocation,
    fetchLocation, getCodes,
    updateLocation,
    addCode, deleteCode, getStaff, deleteStaff, addStaff
} from "../api/locationApi";

import { deleteOffer, newOffer, fetchOffers } from "../api/offerapi";
import { getQrImageForCode } from "../func";
import {getUsers} from "../api/userapi";

export default function Location() {
    const { id } = useParams();
    const { token } = useAuth();
    const [location, setLocation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // ✅ Fetch Location
    const fetchData = async () => {
        setLoading(true);
        try {
            const r = await fetchLocation(id);
            if (r.success) {
                setLocation(r.data);
                setError(null);
            } else {
                setError("Failed to load location.");
            }
        } catch (err) {
            setError(err.message || "Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id, token]);

    if (loading) return <p className="m-3">Loading location...</p>;
    if (error) return <p className="m-3 text-danger">❌ {error}</p>;
    if (!location) return <p className="m-3">No location found.</p>;

    // --- actions ---
    async function removeLocation() {
        try {
            await deleteLocation(id);
            if (window.history.length > 1) navigate(-1);
            else navigate("/locations");
        } catch (err) {
            console.error("Failed to delete location:", err);
        }
    }

    async function saveLocation() {
        updateLocation(location).then(console.log);
    }

    const handleChange = (key, value) => {
        setLocation((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    return (
        <div className="container mt-4">
            <DataCard
                title={"Location details"}
                edit
                remove
                onChange={handleChange}
                onSave={saveLocation}
                onRemove={removeLocation}
                items={[
                    { label: "Name", key: "Name", value: location.Name, editable: true },
                    { label: "Coordinates", key: "Coords", value: location.Coords, editable: true },
                    {
                        label: "Created",
                        value: new Date(location.created_at).toLocaleString(),
                    },
                    {
                        label: "Updated",
                        value: new Date(location.updated_at).toLocaleString(),
                    },
                ]}
            />

            {/* ✅ Tabs section */}
            <Tabs
                tabs={[
                    { key: "offers", label: "Offers", component: <LocationOffersTab locationId={id} /> },
                    { key: "codes", label: "Codes", component: <LocationCodesTab locationId={id} /> },
                    { key: "staff", label: "Staff", component: <LocationStaffTab locationId={id} /> },
                ]}
            />
        </div>
    );
}

/* -------------------------------------------
   ✅ Offers Tab
--------------------------------------------*/
function LocationOffersTab({ locationId }) {
    const navigate = useNavigate();
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOffer, setAddOffer] = useState(false);

    const loadOffers = async () => {
        setLoading(true);
        const res = await fetchLocation(locationId);
        if (res.success) setOffers(res.data.Offers || []);
        setLoading(false);
    };

    const handleAddOffer = async () => {
        await newOffer(locationId);
        loadOffers();
    };

    const handleRemoveOffer = async (offer) => {
        await deleteOffer(offer.OfferID);
        loadOffers();
    };

    useEffect(() => {
        loadOffers();
    }, [locationId]);

    if (loading) return <p>Loading offers...</p>;

    return (
        <>
            <DataTable
                title="Offers at this Location"
                columns={[
                    { key: "Text", label: "Offer", searchable: true },
                    { key: "expires_at", label: "Expires" },
                ]}
                data={(offers || []).map((o) => ({
                    ...o,
                    expires_at: o.expires_at
                        ? new Date(o.expires_at).toLocaleDateString()
                        : "—",
                }))}
                add
                remove
                onAdd={handleAddOffer}
                onRemove={handleRemoveOffer}
                onRowClick={(o) => navigate("/offers/" + o.OfferID)}
            />

            {addOffer && (
                <AddOfferModal
                    show={addOffer}
                    onClose={() => setAddOffer(false)}
                    onAdd={handleAddOffer}
                />
            )}
        </>
    );
}

function AddOfferModal({ show, onClose, onAdd }) {
    const [offers, setOffers] = useState([]);

    useEffect(() => {
        if (show) {
            fetchOffers().then((r) => setOffers(r.data ?? []));
        }
    }, [show]);

    const handleRowClick = (row) => {
        onAdd(row);
        onClose();
    };

    return (
        <CustomModal
            title="Add Offer to Location"
            show={show}
            id="addOfferModal"
            onClose={onClose}
        >
            <DataTable
                title="Available Offers"
                columns={[
                    { key: "Text", label: "Offer", searchable: true },
                    { key: "expires_at", label: "Expires", filterable: true },
                ]}
                data={(offers || []).map((o) => ({
                    ...o,
                    expires_at: o.expires_at
                        ? new Date(o.expires_at).toLocaleDateString()
                        : "—",
                }))}
                onRowClick={handleRowClick}
            />
        </CustomModal>
    );
}

/* -------------------------------------------
   ✅ Codes Tab (similar to Card codes)
--------------------------------------------*/
function LocationCodesTab({ locationId }) {
    const [codes, setCodes] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [qrCode, setQrCode] = useState(null);

    const fetchData = async () => {
        const res = await getCodes(locationId);
        setCodes(res?.data ?? []);
    };

    useEffect(() => {
        fetchData();
    }, [locationId]);

    async function handleAdd(newCodeData) {
        await addCode({...newCodeData, LocationID: locationId});
        fetchData();
    }

    async function handleRemove(row) {

        const prevCodes = [...codes];
        setCodes((prev) => prev.filter((c) => c.LocationCodeID !== row.LocationCodeID));
        try {
            const res = await deleteCode(locationId, row.LocationCodeID);
            if (!res?.success) throw new Error(res?.message || "Failed to remove code");
        } catch (err) {
            setCodes(prevCodes);
        }
    }

    return (
        <>
            <DataTable
                title="Codes"
                rowAction={{
                    title: "QR",
                    onClick: (row) => setQrCode(row.Code),
                }}
                add
                remove
                onAdd={() => setShowAdd(true)}
                onRemove={handleRemove}
                columns={[
                    { key: "Code", label: "Code", searchable: true },
                    { key: "isCountable", label: "Countable" },
                    { key: "Remaining", label: "Remaining" },
                    { key: "expires_at", label: "Expires" },
                ]}
                data={(codes || []).map((c) => ({
                    ...c,
                    isCountable: c.isCountable ? "Yes" : "No",
                    expires_at: c.expires_at
                        ? new Date(c.expires_at).toLocaleDateString()
                        : "—",
                }))}
            />

            {showAdd && (
                <AddCodeModal
                    show={showAdd}
                    onClose={() => setShowAdd(false)}
                    onAdd={handleAdd}
                />
            )}
            {qrCode && <QrCodeModal code={qrCode} onClose={() => setQrCode(null)} />}
        </>
    );
}

/* -------------------------------------------
   ✅ Staff Tab (placeholder for now)
--------------------------------------------*/
function LocationStaffTab({ locationId }) {
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);

    async function fetchStaff() {
        try {
            const r = await getStaff(locationId);
            setStaff(r?.data ?? []);
        } catch (err) {
            console.error("Failed to fetch staff:", err);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchStaff();
    }, [locationId]);

    async function handleAdd(user) {
        try {
            await addStaff(locationId, user.UserID);
            setShowAdd(false);
            fetchStaff();
        } catch (err) {
            console.error("Failed to add staff:", err);
        }
    }

    async function handleRemove(user) {
        const prev = [...staff];
        setStaff((s) => s.filter((u) => u.UserID !== user.UserID));
        try {
            await deleteStaff(locationId, user.UserID);
        } catch (err) {
            console.error("Failed to remove staff:", err);
            setStaff(prev); // rollback
        }
    }

    if (loading) return <p>Loading staff...</p>;

    return (
        <>
            <DataTable
                title="Staff"
                columns={[
                    { key: "Name", label: "Name", searchable: true },
                    { key: "Phone", label: "Phone" },
                ]}
                data={staff}
                add
                remove
                onAdd={() => setShowAdd(true)}
                onRemove={handleRemove}
            />

            {showAdd && (
                <AddStaffModal
                    show={showAdd}
                    onClose={() => setShowAdd(false)}
                    onAdd={handleAdd}
                />
            )}
        </>
    );
}

/* -------------------------------------------
   ✅ AddStaffModal
--------------------------------------------*/
function AddStaffModal({ show, onClose, onAdd }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (show) {
            getUsers()
                .then((r) => setUsers(r?.data ?? []))
                .catch((e) => console.error("Failed to load users:", e))
                .finally(() => setLoading(false));
        }
    }, [show]);

    const handleRowClick = (user) => {
        onAdd(user);
        onClose();
    };

    return (
        <CustomModal title="Add user as staff" show={show} onClose={onClose}>
            {loading ? (
                <p>Loading users...</p>
            ) : (
                <DataTable
                    title="Users"
                    columns={[
                        { key: "Name", label: "Name", searchable: true },
                        { key: "Phone", label: "Phone" },
                    ]}
                    data={users}
                    onRowClick={handleRowClick}
                />
            )}
        </CustomModal>
    );
}

/* -------------------------------------------
   ✅ Shared Modals
--------------------------------------------*/
function AddCodeModal({ show, onClose, onAdd }) {
    const [code, setCode] = useState("");
    const [isCountable, setIsCountable] = useState(false);
    const [remaining, setRemaining] = useState("");
    const [expiresAt, setExpiresAt] = useState("");

    function handleSave() {
        const payload = {
            Code: code || null,
            isCountable,
            Remaining: isCountable ? Number(remaining) || 0 : null,
            expires_at: expiresAt || null,
        };
        onAdd(payload);
        onClose();
    }

    return (
        <CustomModal title="New Code" show={show} onClose={onClose} hideFooter>
            <div className="p-3">
                <div className="mb-3">
                    <label className="form-label">Code (optional)</label>
                    <input
                        className="form-control"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="Leave blank to auto-generate"
                    />
                </div>

                <div className="form-check mb-3">
                    <input
                        type="checkbox"
                        id="countable"
                        className="form-check-input"
                        checked={isCountable}
                        onChange={(e) => setIsCountable(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="countable">
                        Countable (decrease Remaining on use)
                    </label>
                </div>

                {isCountable && (
                    <div className="mb-3">
                        <label className="form-label">Uses</label>
                        <input
                            type="number"
                            className="form-control"
                            value={remaining}
                            onChange={(e) => setRemaining(e.target.value)}
                            min="0"
                            placeholder="e.g. 10"
                        />
                    </div>
                )}

                <div className="mb-3">
                    <label className="form-label">Expires (optional)</label>
                    <input
                        type="date"
                        className="form-control"
                        value={expiresAt}
                        onChange={(e) => setExpiresAt(e.target.value)}
                    />
                </div>

                <div className="d-flex justify-content-end">
                    <button className="btn btn-success" onClick={handleSave}>
                        Save
                    </button>
                </div>
            </div>
        </CustomModal>
    );
}

function QrCodeModal({ code, onClose }) {
    const [imgSrc, setImgSrc] = useState(null);

    useEffect(() => {
        if (code) {
            getQrImageForCode(code).then((src) => setImgSrc(src));
        }
    }, [code]);

    async function handleDownload() {
        try {
            const response = await fetch(imgSrc);
            const blob = await response.blob();

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `QR_${code}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(link.href);
        } catch (err) {
            console.error("Failed to download QR:", err);
        }
    }

    return (
        <CustomModal title="QR Code" show={!!code} onClose={onClose}>
            <div className="text-center p-3">
                <img
                    src={imgSrc}
                    alt={`QR for ${code}`}
                    style={{ maxWidth: "100%", border: "1px solid #ddd" }}
                />
                <div className="mt-3 d-flex justify-content-center gap-2">
                    <button className="btn btn-primary" onClick={handleDownload}>
                        Download
                    </button>
                </div>
            </div>
        </CustomModal>
    );
}
